import React, { useEffect, useMemo, useRef, useState } from 'react';
import Modal from 'components/modal';
import { useTranslation } from 'react-i18next';
import { useUpdateNotificationsMutation } from 'redux_store/services/notifications';
import { formatTimeDifferenceUTC, parseUTCDate } from 'constants/updatedAt';
import { getNotificationChipCategory } from 'pages/notification/constant';
import styles from '../../styles/components/notifications-modal.module.css';
import {
  Bell,
  X,
  Check,
  Circle,
  FileText,
  CheckCircle2,
  Mail,
  ChevronsLeft,
  ChevronLeft,
  ChevronRight,
  ChevronsRight,
} from 'lucide-react';

const PAGE_SIZE = 5;

// How long the bell-panel selection stays spotlighted before fading back.
const HIGHLIGHT_DURATION_MS = 3000;

/** Two-line clamped message with a "Read more" toggle that only renders
 *  when the text actually overflows the clamp. */
const NotificationMessage = ({ text, readMoreLabel, showLessLabel }) => {
  const [expanded, setExpanded] = useState(false);
  const [clamped, setClamped] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    // +1 tolerates sub-pixel rounding so we don't flicker on exact fits.
    setClamped(el.scrollHeight > el.clientHeight + 1);
  }, [text]);

  return (
    <>
      <p
        ref={ref}
        className={expanded ? styles.rowMessageExpanded : styles.rowMessage}
      >
        {text}
      </p>
      {(clamped || expanded) && (
        <button
          type="button"
          className={styles.readMoreButton}
          onClick={() => setExpanded((v) => !v)}
        >
          {expanded ? showLessLabel : readMoreLabel}
        </button>
      )}
    </>
  );
};

const CATEGORY_META = {
  action: { label: 'Action needed', chipClass: 'chipAction', Icon: FileText },
  success: { label: 'Success', chipClass: 'chipSuccess', Icon: CheckCircle2 },
  update: { label: 'Update', chipClass: 'chipUpdate', Icon: Mail },
};

/**
 * "All notifications" dialog (Figma node 1856:2115), opened from the
 * sidebar panel's "View all notifications" button.
 *
 * Read/unread changes are applied optimistically via local overrides
 * alongside the mark-as-read mutation. "Mark unread" is local-only —
 * the backend PUT only supports marking read.
 */
const NotificationsModal = ({
  show,
  onClose,
  notifications = [],
  highlightId,
}) => {
  const { t } = useTranslation(['common']);
  const [updateNotification] = useUpdateNotificationsMutation();
  const [filter, setFilter] = useState('all');
  const [sortDir, setSortDir] = useState('desc');
  const [page, setPage] = useState(1);
  const [overrides, setOverrides] = useState({});
  // The row currently spotlighted because the user picked it in the bell
  // panel. Cleared on a timer so the highlight is transient.
  const [activeHighlight, setActiveHighlight] = useState(null);
  const highlightedIdRef = useRef(null);
  const highlightRowRef = useRef(null);

  const readStateOf = (n) => overrides[n.userNotificationId] ?? n.isRead;
  const unreadCount = notifications.filter((n) => readStateOf(n) === '0').length;

  const visible = useMemo(() => {
    const filtered = notifications.filter(
      (n) => filter === 'all' || readStateOf(n) === '0'
    );
    return [...filtered].sort((a, b) => {
      const diff =
        (parseUTCDate(a.createdAt) || 0) - (parseUTCDate(b.createdAt) || 0);
      return sortDir === 'desc' ? -diff : diff;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [notifications, filter, sortDir, overrides]);

  const pageCount = Math.max(1, Math.ceil(visible.length / PAGE_SIZE));
  const currentPage = Math.min(page, pageCount);
  const pageItems = visible.slice(
    (currentPage - 1) * PAGE_SIZE,
    currentPage * PAGE_SIZE
  );
  const rangeStart = visible.length === 0 ? 0 : (currentPage - 1) * PAGE_SIZE + 1;
  const rangeEnd = Math.min(visible.length, currentPage * PAGE_SIZE);

  // Jump to the page holding the picked notification and spotlight it. The
  // ref guard makes this fire once per highlightId — `visible` is rebuilt
  // whenever overrides change (mark read/unread), and without the guard
  // any of those would restart the highlight.
  useEffect(() => {
    if (!show || !highlightId) return;
    const targetId = String(highlightId);
    if (highlightedIdRef.current === targetId) return;
    const index = visible.findIndex(
      (n) => String(n.userNotificationId) === targetId,
    );
    if (index === -1) return;
    highlightedIdRef.current = targetId;
    setPage(Math.floor(index / PAGE_SIZE) + 1);
    setActiveHighlight(targetId);
  }, [show, highlightId, visible]);

  // Expiry lives in its own effect keyed only on activeHighlight. Keeping the
  // timer in the effect above meant every `visible` rebuild ran that effect's
  // cleanup — cancelling the pending timer — and then hit the ref guard and
  // returned without scheduling a new one, so the highlight never cleared.
  // The mark-as-read that precedes opening this dialog invalidates the
  // notifications query, so a refetch landing mid-highlight did exactly that.
  useEffect(() => {
    if (!activeHighlight) return undefined;
    const timer = setTimeout(
      () => setActiveHighlight(null),
      HIGHLIGHT_DURATION_MS,
    );
    return () => clearTimeout(timer);
  }, [activeHighlight]);

  // Bring the spotlighted row into view once it's on screen. 'nearest'
  // keeps an already-visible row from scrolling needlessly.
  useEffect(() => {
    if (!activeHighlight || !highlightRowRef.current) return;
    highlightRowRef.current.scrollIntoView({
      block: 'nearest',
      behavior: 'smooth',
    });
  }, [activeHighlight, currentPage]);

  const selectFilter = (next) => {
    setFilter(next);
    setPage(1);
  };

  const markRead = (notification) => {
    const id = notification.userNotificationId;
    setOverrides((prev) => ({ ...prev, [id]: '1' }));
    updateNotification({ data: { notification_ids: [id] } });
  };

  const markUnread = (notification) => {
    setOverrides((prev) => ({
      ...prev,
      [notification.userNotificationId]: '0',
    }));
  };

  const markAllRead = () => {
    const unreadIds = notifications
      .filter((n) => readStateOf(n) === '0')
      .map((n) => n.userNotificationId);
    if (unreadIds.length === 0) return;
    setOverrides((prev) => ({
      ...prev,
      ...Object.fromEntries(unreadIds.map((id) => [id, '1'])),
    }));
    updateNotification({ data: { notification_ids: unreadIds } });
  };

  return (
    <Modal show={show} onClose={onClose} width="max-w-[652px]">
      <div className={styles.dialog}>
        <div className={styles.header}>
          <Bell className={styles.headerIcon} />
          <h2 className={styles.title}>
            {t('common:allNotifications', { defaultValue: 'All notifications' })}
          </h2>
          <span className={styles.unreadPill}>
            {t('common:unreadCount', {
              defaultValue: '{{count}} unread',
              count: unreadCount,
            })}
          </span>
          <button
            type="button"
            className={styles.closeButton}
            onClick={onClose}
            aria-label={t('common:buttons.close', {
              defaultValue: 'Close notifications',
              keySeparator: false,
            })}
          >
            <X className={styles.closeIcon} />
          </button>
        </div>

        <div className={styles.tabsRow} role="tablist">
          <button
            type="button"
            role="tab"
            aria-selected={filter === 'all'}
            className={`${styles.tab} ${filter === 'all' ? styles.tabActive : ''}`}
            onClick={() => selectFilter('all')}
          >
            {t('common:all', { defaultValue: 'All' })}
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={filter === 'unread'}
            className={`${styles.tab} ${filter === 'unread' ? styles.tabActive : ''}`}
            onClick={() => selectFilter('unread')}
          >
            {t('common:unread', { defaultValue: 'Unread' })}
          </button>
          <button
            type="button"
            className={styles.tab}
            onClick={() => setSortDir((d) => (d === 'desc' ? 'asc' : 'desc'))}
          >
            {sortDir === 'desc'
              ? t('common:sortNewestFirst', { defaultValue: 'Sort: Newest First' })
              : t('common:sortOldestFirst', { defaultValue: 'Sort: Oldest First' })}
          </button>
        </div>

        <div className={styles.metaRow}>
          <span className={styles.metaText}>
            {filter === 'all'
              ? t('common:showingAllNotifications', {
                  defaultValue: 'Showing all {{count}} notifications',
                  count: visible.length,
                })
              : t('common:showingUnreadNotifications', {
                  defaultValue: 'Showing {{count}} unread notifications',
                  count: visible.length,
                })}
          </span>
          <button
            type="button"
            className={styles.markAllButton}
            onClick={markAllRead}
            disabled={unreadCount === 0}
          >
            {t('common:markAllAsRead', { defaultValue: 'Mark all as read' })}
          </button>
        </div>

        <div className={styles.list}>
          {pageItems.length === 0 && (
            <div className={styles.empty}>
              {t('common:noNewNotifications')}
            </div>
          )}
          {pageItems.map((notification) => {
            const isUnread = readStateOf(notification) === '0';
            const meta =
              CATEGORY_META[getNotificationChipCategory(notification)] ||
              CATEGORY_META.update;
            const ChipIcon = meta.Icon;
            const timeLabel = notification?.createdAt
              ? formatTimeDifferenceUTC(notification.createdAt)
              : '';
            const isHighlighted =
              activeHighlight === String(notification.userNotificationId);
            return (
              <div
                key={notification.userNotificationId}
                ref={isHighlighted ? highlightRowRef : undefined}
                className={`${styles.row} ${isUnread ? styles.rowUnread : ''} ${
                  isHighlighted ? styles.rowHighlight : ''
                }`}
              >
                <div
                  className={`${styles.chip} ${styles[meta.chipClass]} ${
                    isUnread ? '' : styles.chipMutedState
                  }`}
                >
                  <ChipIcon className={styles.chipIcon} />
                </div>
                <div className={styles.rowBody}>
                  <h4
                    className={isUnread ? styles.rowTitle : styles.rowTitleRead}
                  >
                    {notification.subject}
                  </h4>
                  <NotificationMessage
                    text={notification.message}
                    readMoreLabel={t('common:readMore', { defaultValue: 'Read more' })}
                    showLessLabel={t('common:showLess', { defaultValue: 'Show less' })}
                  />
                  <div className={styles.rowMetaRow}>
                    <span className={styles.rowTime}>{timeLabel}</span>
                    <span className={styles.rowTag}>&middot; {meta.label}</span>
                  </div>
                  {isUnread && notification.ctaLabel && (
                    <button
                      type="button"
                      className={styles.rowCta}
                      onClick={() => markRead(notification)}
                    >
                      {notification.ctaLabel}
                    </button>
                  )}
                </div>
                <div className={styles.rowActions}>
                  {isUnread ? (
                    <button
                      type="button"
                      className={styles.markButton}
                      onClick={() => markRead(notification)}
                    >
                      <Check className={styles.markIcon} />
                      {t('common:markRead', { defaultValue: 'Mark read' })}
                    </button>
                  ) : (
                    <button
                      type="button"
                      className={`${styles.markButton} ${styles.markButtonMuted}`}
                      onClick={() => markUnread(notification)}
                    >
                      <Circle className={styles.markIcon} />
                      {t('common:markUnread', { defaultValue: 'Mark unread' })}
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        <div className={styles.footer}>
          <span className={styles.footerText}>
            {t('common:showingEntries', {
              defaultValue: 'Showing {{start}}–{{end}} of {{total}} entries',
              start: rangeStart,
              end: rangeEnd,
              total: visible.length,
            })}
          </span>
          <button
            type="button"
            className={styles.pagerButton}
            onClick={() => setPage(1)}
            disabled={currentPage === 1}
            aria-label={t('common:firstPage', { defaultValue: 'First page' })}
          >
            <ChevronsLeft className={styles.pagerIcon} />
          </button>
          <button
            type="button"
            className={styles.pagerButton}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={currentPage === 1}
            aria-label={t('common:previousPage', { defaultValue: 'Previous page' })}
          >
            <ChevronLeft className={styles.pagerIcon} />
          </button>
          <button
            type="button"
            className={styles.pagerButton}
            onClick={() => setPage((p) => Math.min(pageCount, p + 1))}
            disabled={currentPage === pageCount}
            aria-label={t('common:nextPage', { defaultValue: 'Next page' })}
          >
            <ChevronRight className={styles.pagerIcon} />
          </button>
          <button
            type="button"
            className={styles.pagerButton}
            onClick={() => setPage(pageCount)}
            disabled={currentPage === pageCount}
            aria-label={t('common:lastPage', { defaultValue: 'Last page' })}
          >
            <ChevronsRight className={styles.pagerIcon} />
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default NotificationsModal;
